import { Button, Flex, Stack } from '@mantine/core';
import { useForm, UseFormReturnType } from '@mantine/form';
import { IconPencil } from '@tabler/icons-react';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useUpsertEntity } from 'src/data-capture/merge-review/useUpsertEntities';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { EntitiesByType, EntityMetadata, FormValuesType } from 'src/UI/dynamicUI/types';
import PageLoader from 'src/UI/loader/PageLoader';
import { useSidebarMenu } from 'src/UI/page-nav/MenuTriggerSlot';
import useDynamicNonFinancialData from './hooks/useDynamicNonFinancialData';
import { prepFormValuesForUpsert } from './prepFormValuesForUpsert';
import { buildInitialValues } from './components/utils';
import { ProfileSectionLayout } from './components/ProfileSectionLayout';

export type EntityFormContextType = {
  form: UseFormReturnType<FormValuesType>;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  entities: EntitiesByType;
  entitiesMetadata: { [entityType: string]: EntityMetadata };
  clientId: string;
};

export const EntityFormContext = createContext<EntityFormContextType>({} as EntityFormContextType);

export function useEntityForm() {
  const context = useContext(EntityFormContext);
  if (context === undefined) {
    throw new Error('useEntityForm must be used within an EntityFormProvider');
  }
  return context;
}

type Props = {
  title: string;
  children: ReactNode;
};

export default function EntityFormProvider({ title, children }: Props) {
  useSidebarMenu({ customTrigger: true });
  const { clientId } = useCurrentClient();
  const {
    nonFinancialData,
    isLoading,
    isError,
    refetch: refetchEntities,
  } = useDynamicNonFinancialData();
  const { upsertEntities } = useUpsertEntity();
  const [isEditing, setIsEditing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: {},
  });

  useEffect(() => {
    if ((!isInitialized || !isEditing) && !isLoading && nonFinancialData) {
      const initialValues = buildInitialValues(nonFinancialData);
      form.setInitialValues(initialValues);
      form.setValues(initialValues);
      setIsInitialized(true);
    }
  }, [isLoading, nonFinancialData]);

  if (isLoading || !isInitialized) {
    return <PageLoader absolute={false} />;
  }
  if (isError || !nonFinancialData) {
    return <GenericErrorPage error={{ isError, nonFinancialData }} />;
  }

  const handleSubmit = async (formValues: FormValuesType) => {
    try {
      const valuesToSubmit = prepFormValuesForUpsert(formValues, nonFinancialData, form, false);
      await upsertEntities(clientId, valuesToSubmit, false);
      await refetchEntities();
      form.resetDirty();
      setIsEditing(false);
    } catch (e) {
      notifyAndLogError(
        'Failed to save changes',
        'Something went wrong while saving your changes. Please refresh the page and try again. If the issue persists, please contact support',
        e,
        { formValues },
      );
    }
  };

  const toggleEditing = () => {
    if (isEditing) {
      form.reset();
    }
    setIsEditing(!isEditing);
  };

  const pencilIcon = <IconPencil size={20} />;

  const value = {
    form,
    isEditing,
    setIsEditing,
    entities: nonFinancialData.entities,
    entitiesMetadata: nonFinancialData.ui.entitiesMetadata,
    clientId,
  };

  return (
    <form role="form" onSubmit={form.onSubmit(handleSubmit)}>
      <EntityFormContext value={value}>
        <ProfileSectionLayout
          title={title}
          actions={
            <Flex gap="xs">
              {isEditing && (
                <Button
                  data-testid="save-button"
                  disabled={!form.isDirty()}
                  leftSection={pencilIcon}
                  type="submit"
                  loading={form.submitting}
                >
                  Save
                </Button>
              )}
              <Button
                variant="default"
                onClick={toggleEditing}
                leftSection={!isEditing && pencilIcon}
                data-testid="toggle-edit-button"
              >
                {isEditing ? 'Cancel' : 'Edit'}
              </Button>
            </Flex>
          }
        >
          <Stack gap="xl">{children}</Stack>
        </ProfileSectionLayout>
      </EntityFormContext>
    </form>
  );
}
